import { useCallback, useState } from "react";
import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";
import { GestaoEpi } from "../../domain/models/gestao_epi";
import {
  initialCadastroEpiListaState,
  useCadastroEpiListaFilters,
} from "../hooks/CadastroEpi_lista/filters";
import { useCadastroEpiListaPagination } from "../hooks/CadastroEpi_lista/pagination";
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";
import { processError } from "../../../../common/utils/base_error";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { GestaoEpiForm } from "../schemas/CadastroEpi_lista/gestao_epi";
import {
  useCreateDebounceHandler,
  useDebouncedCallback,
} from "../../../../common/hooks/use_debounced_callback";
import { evaluateParamValue } from "../../../../common/utils/params_utils";

type LoadGestaoEpiListaFuncState = {
  nome?: string | null;
  page?: number;
  rowsPerPage?: number;
};

export const useCadastroEpiListaViewModel = () => {
  const {
    gestaoEpi: {
      getAllGestaoEpiUseCase,
      createGestaoEpiUseCase,
      updateGestaoEpiUseCase,
      deleteGestaoEpiUseCase,
    },
  } = useDependencyInjection();

  const { pushInfoNotification, pushErrorNotification } = usePageLoader();

  const debouncedHandler = useCreateDebounceHandler(750);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>();

  const [gestaoEpiDeleteModal, setGestaoEpiDeleteModal] = useState(false);
  const [gestaoEpiCadastroModal, setGestaoEpiCadastroModal] = useState(false);

  const [gestaoEpiLista, setGestaoEpiLista] = useState<GestaoEpi[]>();
  const [selectedGestaoEpi, setSelectedGestaoEpi] = useState<GestaoEpi>();

  const filters = useCadastroEpiListaFilters();
  const pagination = useCadastroEpiListaPagination();

  //#region  Funcoes de carregamento

  const loadGestaoEpiLista = useCallback(
    async (fs?: LoadGestaoEpiListaFuncState) => {
      try {
        const filteredNome = evaluateParamValue(fs?.nome, filters.nome);

        const page = fs?.page ?? pagination.page;
        const rowsPerPage = fs?.rowsPerPage ?? pagination.rowsPerPage;

        await getAllGestaoEpiUseCase.getAllGestaoEpi(
          filteredNome,
          page,
          rowsPerPage,
        );
        setGestaoEpiLista(getAllGestaoEpiUseCase.items);
        pagination.setTotalItems(getAllGestaoEpiUseCase.totalItems);
      } catch (e: unknown) {
        setGestaoEpiLista(undefined);
        throw new Error("Falha ao buscar lista de epis!");
      }
    },
    [filters, pagination, getAllGestaoEpiUseCase],
  );

  //#endregion

  //#region  useEffect

  usePageStartBootstrap(async () => {
    try {
      await loadGestaoEpiLista();
    } catch (e: unknown) {
      setErrorMessage(processError(e, "Falha ao iniciar pagina!")[0]);
    }
  });

  // #endregion

  //#region Eventos

  const handleReload = useCallback(async () => {
    try {
      setLoading(true);
      await loadGestaoEpiLista();
    } catch (e: unknown) {
      pushErrorNotification(
        "Erro",
        processError(e, "Falha ao recarregar pagina!"),
      );
    } finally {
      setLoading(false);
    }
  }, [loadGestaoEpiLista, pushErrorNotification]);

  const handleCadastroGestaoEpiModalOpenState = useCallback(
    (idGestaoEpi: number | null, openState: boolean) => {
      if (openState) {
        if (gestaoEpiLista && idGestaoEpi) {
          setSelectedGestaoEpi(
            gestaoEpiLista.find((x) => x.idGestaoEpi === idGestaoEpi),
          );
        } else {
          setSelectedGestaoEpi(undefined);
        }
      }
      setGestaoEpiCadastroModal(openState);
    },
    [gestaoEpiLista],
  );

  const handleCadastroGestaoEpiSubmit = useCallback(
    async (form: GestaoEpiForm) => {
      try {
        setSaving(true);
        if (selectedGestaoEpi) {
          await updateGestaoEpiUseCase.updateGestaoEpi(
            selectedGestaoEpi.idGestaoEpi,
            form.nome,
            form.ativo,
            form.especificacao ?? null,
          );
        } else {
          await createGestaoEpiUseCase.createGestaoEpi(
            form.nome,
            form.ativo,
            form.especificacao ?? null,
          );
        }
        setGestaoEpiCadastroModal(false);
        setSelectedGestaoEpi(undefined);
        await loadGestaoEpiLista();
        pushInfoNotification("Sucesso", "Registro salvo com sucesso!");
      } catch (e: unknown) {
        pushErrorNotification(
          "Erro",
          processError(e, "Falha ao salvar registro!"),
        );
      } finally {
        setSaving(false);
      }
    },
    [
      selectedGestaoEpi,
      createGestaoEpiUseCase,
      updateGestaoEpiUseCase,
      loadGestaoEpiLista,
      pushInfoNotification,
      pushErrorNotification,
    ],
  );

  const handleDeleteGestaoEpiModalOpenState = useCallback(
    (idGestaoEpi: number | null, openState: boolean) => {
      if (openState) {
        if (gestaoEpiLista && idGestaoEpi) {
          setSelectedGestaoEpi(
            gestaoEpiLista.find((x) => x.idGestaoEpi === idGestaoEpi),
          );
        } else {
          setSelectedGestaoEpi(undefined);
        }
      }
      setGestaoEpiDeleteModal(openState);
    },
    [gestaoEpiLista],
  );

  const handleDeleteGestaoEpiSubmit = useCallback(async () => {
    try {
      setSaving(true);
      if (selectedGestaoEpi) {
        await deleteGestaoEpiUseCase.deleteGestaoEpi(
          selectedGestaoEpi.idGestaoEpi,
        );
      }
      await loadGestaoEpiLista();
      pushInfoNotification("Sucesso", "Registro salvo com sucesso!");
    } catch (e: unknown) {
      pushErrorNotification(
        "Erro",
        "Este EPI não pode ser excluído pois possui registros de recebimento vinculados a ele.",
      );
    } finally {
      setSelectedGestaoEpi(undefined);
      setSaving(false);
      setGestaoEpiDeleteModal(false);
    }
  }, [
    selectedGestaoEpi,
    deleteGestaoEpiUseCase,
    loadGestaoEpiLista,
    pushInfoNotification,
    pushErrorNotification,
  ]);

  const handlePageChange = useCallback(
    async (page: number) => {
      try {
        setLoading(true);
        useCadastroEpiListaPagination.setState({ page: page + 1 });
        await loadGestaoEpiLista({ page: page + 1 });
      } catch (e: unknown) {
        pushErrorNotification(
          "Erro",
          processError(e, "Falha ao recarregar pagina!"),
        );
      } finally {
        setLoading(false);
      }
    },
    [loadGestaoEpiLista, pushErrorNotification],
  );

  const handleRowsPerPageChange = useCallback(
    async (rowsPerPage: number) => {
      try {
        setLoading(true);
        useCadastroEpiListaPagination.setState({ page: 1, rowsPerPage });
        await loadGestaoEpiLista({ page: 1, rowsPerPage });
      } catch (e: unknown) {
        pushErrorNotification(
          "Erro",
          processError(e, "Falha ao recarregar pagina!"),
        );
      } finally {
        setLoading(false);
      }
    },
    [loadGestaoEpiLista, pushErrorNotification],
  );

  const handleNomeChange = useDebouncedCallback(
    debouncedHandler,
    async (nome: string | null) => {
      useCadastroEpiListaFilters.setState({ nome });
      return async () => {
        try {
          setLoading(true);
          useCadastroEpiListaPagination.setState({ page: 1 });
          await loadGestaoEpiLista({ nome, page: 1 });
        } catch (e: unknown) {
          pushErrorNotification(
            "Erro",
            processError(e, "Falha ao recarregar pagina!"),
          );
        } finally {
          setLoading(false);
        }
      };
    },
    [],
  );

  const handleClearFilters = useCallback(async () => {
    try {
      setLoading(true);
      useCadastroEpiListaFilters.setState({ ...initialCadastroEpiListaState });
      useCadastroEpiListaPagination.setState({ page: 1 });
      await loadGestaoEpiLista({ ...initialCadastroEpiListaState, page: 1 });
    } catch (e: unknown) {
      pushErrorNotification(
        "Erro",
        processError(e, "Falha ao recarregar pagina!"),
      );
    } finally {
      setLoading(false);
    }
  }, [loadGestaoEpiLista, pushErrorNotification]);

  // #endregion

  return {
    loading,
    saving,
    errorMessage,
    gestaoEpiDeleteModal,
    gestaoEpiCadastroModal,
    gestaoEpiLista: gestaoEpiLista ?? [],
    selectedGestaoEpi: selectedGestaoEpi ?? null,
    filters,
    pagination,
    handleReload,
    handleCadastroGestaoEpiModalOpenState,
    handleCadastroGestaoEpiSubmit,
    handleDeleteGestaoEpiModalOpenState,
    handleDeleteGestaoEpiSubmit,
    handlePageChange,
    handleRowsPerPageChange,
    handleNomeChange,
    handleClearFilters,
  };
};
